import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { Layout } from "./components/Layout";
import { UsagePage } from "./pages/UsagePage";
import { checkAuth, LOGIN_URL, LOGGED_OUT_FLAG, type AuthState } from "./api/billingApi";

// Brand styling matched to leiaa-fe: Karla font, #0053FF primary, 16px buttons.
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#0053FF", dark: "#0B25B0", contrastText: "#FFFFFF" },
    text: { primary: "#150A54", secondary: "#69707B" },
    background: { default: "#F7F8FA", paper: "#FFFFFF" },
  },
  typography: {
    fontFamily: ["Karla", "-apple-system", "Roboto", "sans-serif"].join(","),
    button: { fontWeight: 600, textTransform: "none" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 16,
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 20px",
          minHeight: 40,
        },
        containedPrimary: {
          backgroundColor: "#0053FF",
          "&:hover": { backgroundColor: "#0B25B0" },
          "&:active": { backgroundColor: "#140070" },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } } },
    MuiAppBar: {
      styleOverrides: {
        colorDefault: { backgroundColor: "#FFFFFF", color: "#150A54" },
      },
    },
    MuiPaper: { styleOverrides: { rounded: { borderRadius: 12 } } },
  },
});

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        height: "100vh",
      }}
    >
      {children}
    </Box>
  );
}

export function App() {
  const [auth, setAuth] = useState<AuthState | "loading" | "loggedout">("loading");

  useEffect(() => {
    // After an explicit logout, rest on a signed-out screen instead of
    // auto-bouncing back into IAP login.
    if (sessionStorage.getItem(LOGGED_OUT_FLAG) === "1") {
      setAuth("loggedout");
      return;
    }
    checkAuth().then((r) => {
      if (r.state === "unauthenticated") {
        // No IAP session — bounce through the IAP-gated login endpoint once.
        // Guard against a redirect loop if we come back still unauthenticated.
        if (sessionStorage.getItem("iapLoginTried")) {
          setAuth("forbidden");
          return;
        }
        sessionStorage.setItem("iapLoginTried", "1");
        window.location.href = LOGIN_URL;
        return;
      }
      sessionStorage.removeItem("iapLoginTried");
      setAuth(r.state);
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {auth === "loading" || auth === "unauthenticated" ? (
        <Centered>
          <CircularProgress />
          <Typography color="text.secondary">Signing you in…</Typography>
        </Centered>
      ) : auth === "loggedout" ? (
        <Centered>
          <Typography variant="h6">You’ve been signed out</Typography>
          <Button
            variant="contained"
            onClick={() => {
              sessionStorage.removeItem(LOGGED_OUT_FLAG);
              sessionStorage.removeItem("iapLoginTried");
              window.location.href = LOGIN_URL;
            }}
          >
            Log in
          </Button>
        </Centered>
      ) : auth === "forbidden" ? (
        <Centered>
          <Typography variant="h6">Access denied</Typography>
          <Typography color="text.secondary">
            Your account isn’t authorised for the billing admin dashboard.
          </Typography>
        </Centered>
      ) : (
        <Layout>
          <UsagePage />
        </Layout>
      )}
    </ThemeProvider>
  );
}
